import {
	ArrayPick,
	ChildrenProps,
	ClassNameProps,
	Column,
	count,
	englishCommaJoinStrings,
	If,
	makeClassName,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { Employee, EmployeeNameFragment, HomeRoomSection, HomeRoomSectionNameFragment } from "../../../../Utilities";
import { EmployeeFormat, HomeRoomSectionFormat } from "../../../Format";
import { StudentHomeRoomSectionEnrollmentUtilities } from "../../../Utilities";
import * as StudentComponents from "../../Student";
import { HomeRoomSectionIcon } from "../Icon";
import styles from "./styles.module.css";

export type HomeRoomSectionCardProps = {
	homeRoomSection: Pick<HomeRoomSection, "id"> &
		HomeRoomSectionNameFragment & {
			studentHomeRoomSectionEnrollments: ArrayPick<
				HomeRoomSection["studentHomeRoomSectionEnrollments"],
				"id" | "lateStartDate" | "earlyEndDate"
			>;
			teacherHomeRoomSectionEnrollments: { teacher: Pick<Employee, "id"> & EmployeeNameFragment }[];
		};
	slim?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function HomeRoomSectionSimpleCard({
	homeRoomSection,
	slim = false,
	className,
	style,
}: HomeRoomSectionCardProps) {
	const Layout = slim ? Row : Column;

	return (
		<Column
			className={makeClassName(styles["card"], slim ? styles["card--slim"] : styles["card--simple"], className)}
			style={style}
		>
			<div className={styles["card__header"]}>
				<Row justify="space-between" className={styles["card__header__content"]}>
					<Layout
						justify={slim ? "space-between" : "spaced-start"}
						align={slim ? "center" : undefined}
						horizontalSpacing="0.625rem"
						verticalSpacing="0.625rem"
						style={{ flexGrow: 1, minWidth: 0 }}
					>
						<TruncatedText className={styles["card__header__title"]}>
							{HomeRoomSectionFormat.name(homeRoomSection)}
						</TruncatedText>
						<If condition={!slim && homeRoomSection.teacherHomeRoomSectionEnrollments.length > 0}>
							<TruncatedText>
								{englishCommaJoinStrings(
									homeRoomSection.teacherHomeRoomSectionEnrollments.map((e) => EmployeeFormat.name(e.teacher)),
								)}
							</TruncatedText>
						</If>
						<Row justify="spaced-start" horizontalSpacing="0.625rem">
							<StudentsBadge
								studentHomeRoomSectionEnrollments={homeRoomSection.studentHomeRoomSectionEnrollments}
								slim={slim}
							/>
						</Row>
					</Layout>
					<If condition={!slim}>
						<Column justify="center" align="center">
							<HomeRoomSectionIcon homeRoomSection={homeRoomSection} size="4rem" className={styles["card__icon"]} />
						</Column>
					</If>
				</Row>
			</div>
		</Column>
	);
}

export type HomeRoomSectionCustomCardProps = {
	homeRoomSection: Omit<HomeRoomSectionCardProps["homeRoomSection"], "teacherHomeRoomSectionEnrollments">;
} & Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function HomeRoomSectionCustomCard({
	homeRoomSection,
	className,
	style,
	children,
}: HomeRoomSectionCustomCardProps) {
	return (
		<Column className={makeClassName(styles["card"], styles["card--simple"], className)} style={style}>
			<div className={styles["card__header"]}>
				<Row justify="space-between" className={styles["card__header__content"]}>
					<Column
						justify={"spaced-start"}
						horizontalSpacing="0.625rem"
						verticalSpacing="0.625rem"
						style={{ flexGrow: 1, minWidth: 0 }}
					>
						<TruncatedText className={styles["card__header__title"]}>
							{HomeRoomSectionFormat.name(homeRoomSection)}
						</TruncatedText>
						<Row justify="spaced-start" horizontalSpacing="0.625rem">
							<StudentsBadge studentHomeRoomSectionEnrollments={homeRoomSection.studentHomeRoomSectionEnrollments} />
						</Row>
					</Column>
					<Column justify="center" align="center">
						<HomeRoomSectionIcon homeRoomSection={homeRoomSection} size="4rem" className={styles["card__icon"]} />
					</Column>
				</Row>
			</div>

			{children}
		</Column>
	);
}

type StudentsBadgeProps = {
	studentHomeRoomSectionEnrollments: HomeRoomSectionCardProps["homeRoomSection"]["studentHomeRoomSectionEnrollments"];
	slim?: boolean;
};

function StudentsBadge({ studentHomeRoomSectionEnrollments, slim }: StudentsBadgeProps) {
	const numActive = count(studentHomeRoomSectionEnrollments, (e) =>
		StudentHomeRoomSectionEnrollmentUtilities.isActive(e),
	);

	return <StudentComponents.StudentsCountBadge count={numActive} slim={slim} className={styles["card__badge"]} />;
}
